<template>
  <!-- Recherche-Anzeige: die Schritte folgen den status-Ereignissen des
       Servers (knowledge → context → thinking → writing). Erledigte Schritte
       bekommen ein Häkchen, der aktuelle schimmert. -->
  <div aria-live="polite" class="ai-thinking ai-rise" role="status">
    <span aria-hidden="true" class="ai-orb ai-thinking__orb">
      <Sparkles :size="14" />
    </span>
    <ol class="ai-thinking__steps">
      <li
        v-for="(item, index) in steps"
        :key="item.key"
        class="ai-thinking__step"
        :class="{ 'is-done': index < currentIndex, 'is-current': index === currentIndex, 'is-pending': index > currentIndex }"
      >
        <span aria-hidden="true" class="ai-thinking__mark">
          <Check v-if="index < currentIndex" :size="12" />
          <span v-else-if="index === currentIndex" class="ai-thinking__dots"><i /><i /><i /></span>
        </span>
        <span :class="{ 'ai-shimmer': index === currentIndex }">{{ index === currentIndex && stepText ? stepText : item.label }}</span>
      </li>
    </ol>
  </div>
</template>

<script setup lang="ts">
  import type { AssistantStep } from '@/services/assistantApi'
  import { Check, Sparkles } from 'lucide-vue-next'
  import { computed } from 'vue'
  import { gt } from '@/i18n/gt'

  const props = defineProps<{
    step: AssistantStep | null
    stepText?: string
  }>()

  const steps = computed(() => [
    { key: 'knowledge', label: gt('Durchsuche das Hilfe-Center') },
    { key: 'context', label: gt('Lese Ihre Einstellungen und Aufgaben') },
    { key: 'thinking', label: gt('Formuliere die Antwort') },
  ])

  const currentIndex = computed(() => {
    const order: AssistantStep[] = ['knowledge', 'context', 'thinking', 'writing']
    const idx = props.step ? order.indexOf(props.step) : 0
    return Math.min(Math.max(idx, 0), steps.value.length)
  })
</script>

<style scoped>

  .ai-thinking {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    max-width: 88%;
    padding: 12px 14px;
    border-radius: 18px 18px 18px 6px;
    background: #fff;
    border: 1px solid var(--ai-line);
    box-shadow: 0 6px 18px rgba(16, 24, 40, 0.06);
  }

  .ai-thinking__orb {
    flex: none;
    width: 26px;
    height: 26px;
    margin-top: 1px;
  }

  .ai-thinking__steps {
    display: grid;
    gap: 6px;
    margin: 0;
    padding: 0;
    list-style: none;
    font-size: 13px;
  }

  .ai-thinking__step {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--ai-muted);
    transition: opacity 240ms var(--ai-ease);
  }

  .ai-thinking__step.is-pending {
    opacity: 0.45;
  }

  .ai-thinking__step.is-done {
    color: var(--ai-ink);
  }

  .ai-thinking__mark {
    display: inline-grid;
    place-items: center;
    width: 18px;
    height: 18px;
    border-radius: 999px;
    background: var(--ai-surface-2);
    color: #16a34a;
  }

  .is-done .ai-thinking__mark {
    background: #e7f8ec;
    animation: ai-pop 320ms var(--ai-ease) both;
  }

  .ai-thinking__dots {
    display: inline-flex;
    gap: 2px;
  }

  .ai-thinking__dots i {
    width: 4px;
    height: 4px;
    border-radius: 999px;
    background: var(--ai-indigo);
    animation: ai-dot 1.1s ease-in-out infinite;
  }

  .ai-thinking__dots i:nth-child(2) {
    animation-delay: 0.15s;
  }

  .ai-thinking__dots i:nth-child(3) {
    animation-delay: 0.3s;
  }

  @media (prefers-reduced-motion: reduce) {
    .ai-thinking__dots i,
    .is-done .ai-thinking__mark {
      animation: none !important;
    }
  }
</style>
